import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { projectsAPI, tasksAPI, usersAPI } from '../lib/api';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { 
  MapPin, Calendar, FileText, CheckSquare, Clock, User, Users, Pencil,
  Loader2, Image, FileArchive, ClipboardList, DollarSign, Activity,
  FileCheck, GitPullRequest, Timer, DoorOpen, Milestone, Camera
} from 'lucide-react';
import ProjectChecklists from '../components/ProjectChecklists';
import ProjectBudget from '../components/ProjectBudget';
import CommentsSection from '../components/CommentsSection';
import ActivityLog from '../components/ActivityLog';
import ProjectApprovals from '../components/ProjectApprovals';
import ProjectChangeOrders from '../components/ProjectChangeOrders';
import TimeTracking from '../components/TimeTracking';
import MoodBoards from '../components/MoodBoards';
import DocumentManagement from '../components/DocumentManagement';
import ProjectRooms from '../components/ProjectRooms';
import ProjectPhases from '../components/ProjectPhases';
import SiteDiary from '../components/SiteDiary';
import { ProjectTasksTab } from '../components/project/ProjectTasksTab';
import { ProjectFilesTab } from '../components/project/ProjectFilesTab';
import { ClientAccessDialog } from '../components/project/ClientAccessDialog';
import { ProjectTaskDialog } from '../components/project/ProjectTaskDialog';

export default function ProjectDetail() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const defaultTab = searchParams.get('tab') || 'tasks';
  const { user, isAdmin, isStaff } = useAuth();
  const canEdit = isAdmin || isStaff;
  const canUpload = isAdmin || isStaff;
  const canDelete = isAdmin || isStaff;
  const fileInputRef = useRef(null);
  
  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  
  const [clientDialogOpen, setClientDialogOpen] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [selectedViewers, setSelectedViewers] = useState([]);
  
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [taskForm, setTaskForm] = useState({
    title: '', description: '', due_date: '', priority: 'medium', status: 'todo', assignees: [],
  });
  const [taskLoading, setTaskLoading] = useState(false);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [projectRes, tasksRes] = await Promise.all([
        projectsAPI.get(projectId),
        tasksAPI.list(projectId),
      ]);
      setProject(projectRes.data);
      setTasks(tasksRes.data);
      setEditData(projectRes.data);
      setSelectedClientId(projectRes.data.client_id || '');
      setSelectedViewers(projectRes.data.allowed_viewers || []);
      if (isAdmin || isStaff) {
        try {
          const usersRes = isAdmin ? await usersAPI.list() : await usersAPI.teamMembers();
          setAllUsers(usersRes.data);
        } catch (err) {
          console.error('Could not fetch users:', err);
        }
      }
    } catch (err) {
      console.error('Failed to fetch project:', err);
      navigate('/projects');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProject = async () => {
    setSaving(true);
    try {
      const res = await projectsAPI.update(projectId, {
        name: editData.name, city: editData.city,
        description: editData.description, status: editData.status,
      });
      setProject(res.data);
      setEditMode(false);
    } catch (err) {
      console.error('Failed to update project:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveClientAssignment = async () => {
    setSaving(true);
    try {
      const res = await projectsAPI.update(projectId, {
        client_id: selectedClientId || null, allowed_viewers: selectedViewers,
      });
      setProject(res.data);
      setClientDialogOpen(false);
    } catch (err) {
      console.error('Failed to update client assignment:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await projectsAPI.uploadFile(projectId, file);
      setProject({ ...project, files: [...(project.files || []), res.data] });
    } catch (err) {
      console.error('Failed to upload file:', err);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeleteFile = async (fileId) => {
    try {
      await projectsAPI.deleteFile(projectId, fileId);
      setProject({ ...project, files: project.files.filter(f => f.file_id !== fileId) });
    } catch (err) {
      console.error('Failed to delete file:', err);
    }
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    setTaskLoading(true);
    try {
      const res = await tasksAPI.create({
        ...taskForm, project_id: projectId,
        due_date: taskForm.due_date ? new Date(taskForm.due_date).toISOString() : null,
      });
      setTasks([res.data, ...tasks]);
      setTaskDialogOpen(false);
      setTaskForm({ title: '', description: '', due_date: '', priority: 'medium', status: 'todo', assignees: [] });
    } catch (err) {
      console.error('Failed to create task:', err);
    } finally {
      setTaskLoading(false);
    }
  };

  const handleTaskStatusChange = async (taskId, newStatus) => {
    try {
      await tasksAPI.update(taskId, { status: newStatus });
      setTasks(tasks.map(t => t.task_id === taskId ? { ...t, status: newStatus } : t));
    } catch (err) {
      console.error('Failed to update task:', err);
    }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      await tasksAPI.delete(taskId);
      setTasks(tasks.filter(t => t.task_id !== taskId));
    } catch (err) {
      console.error('Failed to delete task:', err);
    }
  };

  const toggleViewer = (userId) => {
    setSelectedViewers(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  // Helpers
  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'status-completed';
      case 'in_progress': return 'status-in_progress';
      case 'on_hold': return 'status-on_hold';
      default: return 'status-planning';
    }
  };
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'priority-high';
      case 'medium': return 'priority-medium';
      default: return 'priority-low';
    }
  };
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };
  const getAssigneeNames = (task) => {
    const assignees = task.assignees || (task.assignee_id ? [task.assignee_id] : []);
    if (assignees.length === 0) return '-';
    return assignees.map(id => allUsers.find(u => u.user_id === id)?.name || 'Unknown').join(', ');
  };
  const getClientName = (clientId) => {
    if (!clientId) return 'Not Assigned';
    return allUsers.find(u => u.user_id === clientId)?.name || 'Unknown';
  };

  const completedTasks = tasks.filter(t => t.status === 'completed').length;
  const taskProgress = tasks.length > 0 ? Math.round((completedTasks / tasks.length) * 100) : 0;
  const clients = allUsers.filter(u => u.role === 'client');
  const staffMembers = allUsers.filter(u => u.role !== 'client');

  if (loading) {
    return (
      <Layout>
        <div className="space-y-6 animate-pulse">
          <div className="h-8 bg-muted w-1/4 rounded" />
          <div className="h-64 bg-muted rounded" />
        </div>
      </Layout>
    );
  }

  if (!project) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-muted-foreground">Project not found</p>
          <Button onClick={() => navigate('/projects')} className="mt-4">Back to Projects</Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="project-detail-page">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Link to="/projects" className="hover:text-foreground transition-colors">Projects</Link>
          <span>/</span>
          <span className="text-foreground">{project.name}</span>
        </div>

        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div className="flex-1">
            {editMode ? (
              <div className="space-y-4 max-w-xl">
                <div className="space-y-2">
                  <Label className="form-label">Project Name</Label>
                  <Input value={editData.name} onChange={(e) => setEditData({ ...editData, name: e.target.value })} data-testid="edit-project-name" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="form-label">City</Label>
                    <Input value={editData.city} onChange={(e) => setEditData({ ...editData, city: e.target.value })} data-testid="edit-project-city" />
                  </div>
                  <div className="space-y-2">
                    <Label className="form-label">Status</Label>
                    <Select value={editData.status} onValueChange={(value) => setEditData({ ...editData, status: value })}>
                      <SelectTrigger data-testid="edit-project-status"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="planning">Planning</SelectItem>
                        <SelectItem value="in_progress">In Progress</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                        <SelectItem value="on_hold">On Hold</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="form-label">Description</Label>
                  <Textarea value={editData.description || ''} onChange={(e) => setEditData({ ...editData, description: e.target.value })} rows={3} data-testid="edit-project-description" />
                </div>
                <div className="flex gap-2">
                  <Button onClick={handleSaveProject} disabled={saving} className="btn-primary" data-testid="save-project-btn">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Save Changes
                  </Button>
                  <Button variant="outline" onClick={() => { setEditMode(false); setEditData(project); }}>Cancel</Button>
                </div>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-3 mb-2">
                  <h1 className="text-3xl md:text-4xl font-light tracking-tight">{project.name}</h1>
                  <span className={`status-badge ${getStatusColor(project.status)}`}>{project.status.replace('_', ' ')}</span>
                </div>
                <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1"><MapPin className="h-4 w-4" />{project.city}</span>
                  <span className="flex items-center gap-1"><Calendar className="h-4 w-4" />Created {formatDate(project.created_at)}</span>
                  <span className="flex items-center gap-1"><FileText className="h-4 w-4" />{project.files?.length || 0} files</span>
                  <span className="flex items-center gap-1"><User className="h-4 w-4" />Client: {getClientName(project.client_id)}</span>
                </div>
                {project.description && <p className="text-muted-foreground mt-4 max-w-2xl">{project.description}</p>}
              </>
            )}
          </div>
          {!editMode && canEdit && (
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setClientDialogOpen(true)} data-testid="assign-client-btn">
                <Users className="h-4 w-4 mr-2" />Manage Access
              </Button>
              <Button variant="outline" onClick={() => setEditMode(true)} data-testid="edit-project-btn">
                <Pencil className="h-4 w-4 mr-2" />Edit
              </Button>
            </div>
          )}
        </div>

        {/* Progress */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-medium">Project Progress</h3>
                <p className="text-sm text-muted-foreground">{completedTasks} of {tasks.length} tasks completed</p>
              </div>
              <span className="text-2xl font-light">{taskProgress}%</span>
            </div>
            <Progress value={taskProgress} className="h-2" />
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs defaultValue={defaultTab} className="space-y-6">
          <TabsList className="w-full overflow-x-auto flex flex-nowrap justify-start md:flex-wrap md:justify-center scrollbar-hide">
            <TabsTrigger value="tasks" data-testid="tasks-tab"><CheckSquare className="h-4 w-4 mr-2" />Tasks ({tasks.length})</TabsTrigger>
            <TabsTrigger value="rooms" data-testid="rooms-tab"><DoorOpen className="h-4 w-4 mr-2" />Rooms</TabsTrigger>
            <TabsTrigger value="phases" data-testid="phases-tab"><Milestone className="h-4 w-4 mr-2" />Phases</TabsTrigger>
            <TabsTrigger value="diary" data-testid="diary-tab"><Camera className="h-4 w-4 mr-2" />Site Diary</TabsTrigger>
            <TabsTrigger value="checklists" data-testid="checklists-tab"><ClipboardList className="h-4 w-4 mr-2" />Checklists</TabsTrigger>
            <TabsTrigger value="budget" data-testid="budget-tab"><DollarSign className="h-4 w-4 mr-2" />Budget</TabsTrigger>
            <TabsTrigger value="files" data-testid="files-tab"><FileText className="h-4 w-4 mr-2" />Files ({project.files?.length || 0})</TabsTrigger>
            <TabsTrigger value="activity" data-testid="activity-tab"><Activity className="h-4 w-4 mr-2" />Activity</TabsTrigger>
            <TabsTrigger value="approvals" data-testid="approvals-tab"><FileCheck className="h-4 w-4 mr-2" />Approvals</TabsTrigger>
            <TabsTrigger value="change-orders" data-testid="change-orders-tab"><GitPullRequest className="h-4 w-4 mr-2" />Change Orders</TabsTrigger>
            <TabsTrigger value="time" data-testid="time-tab"><Timer className="h-4 w-4 mr-2" />Time</TabsTrigger>
            <TabsTrigger value="mood-boards" data-testid="mood-boards-tab"><Image className="h-4 w-4 mr-2" />Mood Boards</TabsTrigger>
            <TabsTrigger value="documents" data-testid="documents-tab"><FileArchive className="h-4 w-4 mr-2" />Documents</TabsTrigger>
          </TabsList>

          <TabsContent value="tasks" className="space-y-4">
            <ProjectTasksTab
              tasks={tasks} isAdmin={isAdmin} isStaff={isStaff}
              onAddTask={() => setTaskDialogOpen(true)}
              onStatusChange={handleTaskStatusChange}
              onDeleteTask={handleDeleteTask}
              formatDate={formatDate} getPriorityColor={getPriorityColor}
              getAssigneeNames={getAssigneeNames}
            />
          </TabsContent>
          <TabsContent value="checklists" className="space-y-4"><ProjectChecklists projectId={projectId} /></TabsContent>
          <TabsContent value="rooms" className="space-y-4"><ProjectRooms projectId={projectId} /></TabsContent>
          <TabsContent value="phases" className="space-y-4"><ProjectPhases projectId={projectId} /></TabsContent>
          <TabsContent value="diary" className="space-y-4"><SiteDiary projectId={projectId} /></TabsContent>
          <TabsContent value="files" className="space-y-4">
            <ProjectFilesTab
              files={project.files} onFileUpload={handleFileUpload}
              onDeleteFile={handleDeleteFile} uploading={uploading}
              fileInputRef={fileInputRef} canUpload={canUpload}
              canDelete={canDelete} formatDate={formatDate}
            />
          </TabsContent>
          <TabsContent value="budget" className="space-y-4"><ProjectBudget projectId={projectId} /></TabsContent>
          <TabsContent value="activity" className="space-y-6">
            <Card><CardHeader><CardTitle className="text-base">Project Comments</CardTitle></CardHeader><CardContent><CommentsSection entityType="project" entityId={projectId} /></CardContent></Card>
            <Card><CardHeader><CardTitle className="text-base">Activity Timeline</CardTitle></CardHeader><CardContent><ActivityLog projectId={projectId} /></CardContent></Card>
          </TabsContent>
          <TabsContent value="approvals" className="space-y-4"><ProjectApprovals projectId={projectId} /></TabsContent>
          <TabsContent value="change-orders" className="space-y-4"><ProjectChangeOrders projectId={projectId} /></TabsContent>
          <TabsContent value="time" className="space-y-4"><TimeTracking projectId={projectId} tasks={tasks} /></TabsContent>
          <TabsContent value="mood-boards" className="space-y-4"><MoodBoards projectId={projectId} /></TabsContent>
          <TabsContent value="documents" className="space-y-4"><DocumentManagement projectId={projectId} /></TabsContent>
        </Tabs>

        {/* Dialogs */}
        <ClientAccessDialog
          open={clientDialogOpen} onOpenChange={setClientDialogOpen}
          clients={clients} allUsers={allUsers}
          selectedClientId={selectedClientId} setSelectedClientId={setSelectedClientId}
          selectedViewers={selectedViewers} toggleViewer={toggleViewer}
          onSave={handleSaveClientAssignment} saving={saving}
          currentUserId={user?.user_id}
        />

        <ProjectTaskDialog
          open={taskDialogOpen} onOpenChange={setTaskDialogOpen}
          taskForm={taskForm} setTaskForm={setTaskForm}
          onSubmit={handleCreateTask} taskLoading={taskLoading}
          staffMembers={staffMembers}
        />
      </div>
    </Layout>
  );
}
